"use client";

import React, { useCallback, useMemo, useContext } from "react";
import { usePathname } from "next/navigation";
import { FormContext } from "@/contexts/FormDataContext";
import { useSchema } from "@/contexts/FormSchemaContext";
import { GlobalContext } from "@/contexts/SiteDataContext";
import { FormStepType } from "@/data/forms/types";
import classNames from "classnames";
import "./styles.scss";

interface StepperProps {
  completed?: boolean;
}

const Stepper: React.FC<StepperProps> = ({ completed = false }) => {
  const { formSchema: formSteps } = useSchema();
  const { step, setStep, completedSteps } = useContext(FormContext)!;
  const { t } = useContext(GlobalContext).context;
  const pathname = usePathname();

  const isEditingForm = pathname.startsWith("/edit");
  const isReviewPage = pathname.includes("/review");

  const finalSteps = useMemo<FormStepType[]>(() => {
    if (!formSteps) return [];
    return [
      ...formSteps,
      {
        id: formSteps.length + 1,
        name: "review-application",
        title: `${formSteps.length + 1}. Review Application`,
        groups: [],
      },
    ];
  }, [formSteps]);

  const isStepCompleted = useCallback(
    (stepIndex: number) => completed || completedSteps.includes(stepIndex),
    [completed, completedSteps]
  );

  const handleStepClick = (index: number, isCompleted: boolean) => {
    if (!completed && isCompleted) {
      setStep(index);
    }
  };

  if (!formSteps) return null;

  return (
    <div className="Stepper">
      {finalSteps.map((stepItem, index) => {
        const isLastStep = index === finalSteps.length - 1;
        const isActiveStep = (!isReviewPage && index === step) || (isReviewPage && isLastStep);
        const isCompleted = isStepCompleted(index);
        let stepIndexLabel: number | undefined = index + 1;

        if (isCompleted && !(isActiveStep || isEditingForm)) {
          stepIndexLabel = undefined; // If it's completed, but not the active step and not in editing mode, leave it empty
        }

        return (
          <div
            key={stepItem.id}
            onClick={() => handleStepClick(index, isCompleted)}
            className={classNames("Stepper__step", {
              ["Stepper__step--active"]: isActiveStep,
              ["Stepper__step--complete"]:
                (!isActiveStep && isCompleted && !isEditingForm) || (isReviewPage && !isLastStep),
              ["Stepper__step--incomplete"]: !isCompleted || isEditingForm,
              ["Stepper__step--disabled"]: completed || isEditingForm || isReviewPage,
            })}
            aria-label={`Step ${index + 1}: ${stepItem.title}`}
            role="button"
            data-qa={`stepper-${stepItem.name}`}>
            <span className="Stepper__dot">{stepIndexLabel}</span>
            <span
              className="Stepper__info small"
              dangerouslySetInnerHTML={{ __html: t[stepItem.title].substring(3) }}></span>
          </div>
        );
      })}
    </div>
  );
};

export default Stepper;
